import React, { useState, useEffect } from "react";
import Header from "@/components/Header";
import { useWatchlist } from "@/context/WatchlistContext";
import { WatchlistTabs } from "@/components/WatchlistTabs";
import { TradingFeed } from "@/components/TradingFeed";
import { Eye, Plus, Search, X, Check, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { usePriceContext } from "@/context/PriceContext";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const Watchlist: React.FC = () => {
  const { watchlists, activeWatchlistId, watchlistSymbols, addToWatchlist, addMultipleToWatchlist, setActiveView } = useWatchlist();
  const { symbols: allSymbols } = usePriceContext();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const isEmpty = !watchlistSymbols.length;
  
  // Filter symbols based on search and exclude symbols that are already in the watchlist
  const filteredSymbols = allSymbols
    .filter(symbol => 
      symbol.name.toLowerCase().includes(searchValue.toLowerCase()) && 
      !watchlistSymbols.includes(symbol.name)
    )
    .slice(0, 50); // Limit results for better performance
  
  const handleAddSymbol = (symbolName: string) => {
    // Single symbol add (kept for backward compatibility)
    addToWatchlist(symbolName);
    setSearchValue("");
    toast.success(`${symbolName} added to watchlist`, {
      id: `add-${symbolName}`,
      duration: 3000,
    });
  };
  
  const handleSymbolToggle = (symbolName: string) => {
    setSelectedSymbols(prev => 
      prev.includes(symbolName) 
        ? prev.filter(s => s !== symbolName)
        : [...prev, symbolName]
    );
  };
  
  const handleAddSelectedSymbols = async () => {
    if (selectedSymbols.length === 0) {
      toast.error("No symbols selected");
      return;
    }
    
    // Add all selected symbols to the watchlist in a single API call
    await addMultipleToWatchlist(selectedSymbols);
    
    // Reset state
    setSelectedSymbols([]);
    setSearchValue("");
    setIsAddDialogOpen(false);
  };
  
  // Reset selected symbols when dialog closes
  useEffect(() => {
    if (!isAddDialogOpen) {
      setSelectedSymbols([]);
      setSearchValue("");
    }
  }, [isAddDialogOpen]);
  
  // Ensure we're in the correct view mode for swipe functionality
  useEffect(() => {
    // Set to 'favorites' view to enable swipe functionality
    setActiveView('favorites');
  }, [setActiveView]);
  
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <style>
        {`
          /* Prevent layout shifts in tables */
          .market-table {
            table-layout: fixed;
          }
          
          .market-table th, .market-table td {
            width: auto;
            min-width: 80px;
            transition: none !important;
          }
          
          /* Prevent price animation flickering */
          .price-cell {
            width: 90px;
            min-width: 90px;
            text-align: right;
          }
          
          /* Add trade now button styling */
          .trade-now-btn {
            white-space: nowrap;
            font-size: 0.75rem;
          }
        `}
      </style>
      <Header />
      <main className="flex-1 watch-list">
        <div className="mx-auto px-4 ">
          <div className="mb-4 ">
            <WatchlistTabs />
          </div>
          
          {watchlists.length === 0 ? (
            <EmptyState 
              icon={<Eye className="h-12 w-12 text-muted-foreground" />}
              title="No watchlists available"
              description="Create a watchlist to get started"
              action={
                <Button onClick={() => {
                  setIsAddDialogOpen(false); // Close any open dialog first
                  const defaultId = Math.random().toString(36).substring(2, 10);
                  const defaultWatchlist = {
                    id: defaultId,
                    name: "Default",
                    symbols: []
                  };
                  
                  // Force a refresh by setting timeout
                  setTimeout(() => {
                    // This will be handled by the context's createWatchlist
                    const nameInput = document.querySelector('input[name="new-watchlist-name"]');
                    if (nameInput instanceof HTMLInputElement) {
                      nameInput.value = "Default";
                    }
                    const createButton = document.querySelector('[data-create-watchlist]');
                    if (createButton instanceof HTMLElement) {
                      createButton.click();
                    }
                  }, 0);
                }}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Watchlist
                </Button>
              }
            />
          ) : isEmpty ? (
            <EmptyState 
              icon={<Eye className="h-12 w-12 text-muted-foreground" />}
              title="Your watchlist is empty"
              description="Add symbols to get started"
              action={
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Symbols
                </Button>
              }
            />
          ) : (
            <TradingFeed hideStarInWatchlist={true} />
          )}
        </div>
      </main>
      
      {/* Multi-select Add Symbols Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Symbols to Watchlist</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Command className="rounded-lg border shadow-md">
              <div className="relative">
                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                                      <Input
                                        placeholder="Search symbols..."
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        className="pl-10 pr-10"
                                      />
              </div>
              {filteredSymbols.length > 0 ? (
                <div className="border-t">
                  <div className="py-2 px-2 flex justify-between items-center border-b">
                    <span className="text-sm font-medium">Select symbols</span>
                    <div className="flex space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-7"
                        onClick={() => setSelectedSymbols(filteredSymbols.map(s => s.name))}
                      >
                        Select All
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-7"
                        onClick={() => setSelectedSymbols([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[300px]" preserveScrollOffset={true}>
                    <CommandList>
                      <CommandGroup>
                        {filteredSymbols.map((symbol) => (
                          <CommandItem
                            key={symbol.name}
                            value={symbol.name}
                            className="flex items-center justify-between cursor-pointer"
                          >
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                checked={selectedSymbols.includes(symbol.name)}
                                onCheckedChange={() => handleSymbolToggle(symbol.name)}
                                id={`symbol-${symbol.name}`}
                              />
                              <label 
                                htmlFor={`symbol-${symbol.name}`} 
                                className="text-sm cursor-pointer flex-1"
                                onClick={() => handleSymbolToggle(symbol.name)}
                              >
                                {symbol.name}
                              </label>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </ScrollArea>
                </div>
              ) : searchValue ? (
                <div className="py-6 px-4 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Search className="h-12 w-12 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No symbols found</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                </div>
              ) : (
                <CommandEmpty className="py-6 text-center text-sm">
                  Type to search for symbols
                </CommandEmpty>
              )}
            </Command>
          </div>
          
          {selectedSymbols.length > 0 && (
            <div className="flex items-center justify-between py-2 px-1">
              <span className="text-sm text-muted-foreground">
                {selectedSymbols.length} symbol{selectedSymbols.length !== 1 ? 's' : ''} selected
              </span>
            </div>
          )}
          
          <DialogFooter className="flex justify-between space-x-2">
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddSelectedSymbols} 
              disabled={selectedSymbols.length === 0}
            >
              Add Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Watchlist;
